// src/features/nonconformita/NonConformitaPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";

const CATEGORIES = [
    { value: "processo_lavorazione", label: "Processo di lavorazione" },
    { value: "controllo_temperature_conservazione", label: "Controllo temperature di conservazione" },
    { value: "analisi_microbiologiche", label: "Analisi microbiologiche" },
    { value: "stoccaggio_prodotto_finito", label: "Stoccaggio prodotto finito" },
    { value: "altro", label: "Altro" },
];

function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

export default function NonConformitaPage() {
    const readOnly = new URLSearchParams(window.location.search).get("readonly") === "1";
    const [status, setStatus] = useState("open");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        p.set("status", status);
        if (from) p.set("from", from);
        if (to) p.set("to", to);
        return p.toString();
    }, [status, from, to]);

    async function load() {
        setLoading(true);
        try {
            const res = await fetch(`/api/nonconformita/list.php?${qs}`, {
                credentials: "include",
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || "Errore caricamento");
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch (e) {
            console.error(e);
            setItems([]);
            alert("Errore nel caricamento delle non conformità.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qs]);

    function openNew() {
        setEditing({
            id: null,
            data: todayISO(),
            rilevata_da: "",
            categoria: "controllo_temperature_conservazione",
            categoria_altro: "",
            descrizione: "",
            cause_ipotesi: "",
            trattamento: "",
            firma_responsabile: "",
            firma_rilevatore: "",
            verifica_azione_correttiva: "",
            verifica_data: "",
            verifica_firma_responsabile: "",
        });
        setModalOpen(true);
    }

    function openEdit(row) {
        setEditing({
            ...row,
            categoria_altro: row.categoria_altro || "",
            rilevata_da: row.rilevata_da || "",
            cause_ipotesi: row.cause_ipotesi || "",
            trattamento: row.trattamento || "",
            firma_responsabile: row.firma_responsabile || "",
            firma_rilevatore: row.firma_rilevatore || "",
            verifica_azione_correttiva: row.verifica_azione_correttiva || "",
            verifica_data: row.verifica_data || "",
            verifica_firma_responsabile: row.verifica_firma_responsabile || "",
        });
        setModalOpen(true);
    }

    async function save() {
        if (!editing) return;

        const isNew = !editing.id;
        const url = isNew ? "/api/nonconformita/create.php" : "/api/nonconformita/update.php";

        if (!editing.data || !editing.descrizione || !editing.categoria) {
            alert("Compila almeno Data, Categoria e Descrizione.");
            return;
        }
        if (editing.categoria === "altro" && !editing.categoria_altro.trim()) {
            alert("Hai scelto 'Altro': specifica il testo.");
            return;
        }

        try {
            const res = await fetch(url, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editing),
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || "Errore salvataggio");
            setModalOpen(false);
            setEditing(null);
            load();
        } catch (e) {
            console.error(e);
            alert("Errore durante il salvataggio.");
        }
    }

    async function closeNC(id) {
        if (!window.confirm("Vuoi chiudere questa non conformità?")) return;
        try {
            const res = await fetch("/api/nonconformita/close.php", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || "Errore chiusura");
            load();
        } catch (e) {
            console.error(e);
            alert("Errore durante la chiusura.");
        }
    }

    // ✅ PDF: stampa lista filtrata (carta intestata + filigrana gestite lato PHP)
    function printPdfList() {
        const url = `/api/nonconformita/pdf.php?${qs}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }

    // ✅ PDF: stampa singola NC
    function printPdfOne(id) {
        const url = `/api/nonconformita/pdf-one.php?id=${encodeURIComponent(id)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }

    return (
        <>
            <TopBar title="NORMA HACCP — Non Conformità" showBack />

            <div className="min-h-screen bg-slate-100 p-4 md:p-6">
                <div className="max-w-6xl mx-auto bg-white shadow-md rounded-2xl p-4 md:p-6">
                    {/* Header + azioni */}
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-2xl font-semibold text-slate-800">{readOnly ? "Storico Non Conformità" : "Modulo Non Conformità"}</div>
                            <div className="text-sm text-slate-500">
                                Digitalizzazione Allegato 4 — gestione, verifica e chiusura
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={printPdfList}
                                className="px-4 py-2 rounded-xl border bg-white hover:bg-slate-50"
                                title="Stampa PDF elenco (filtri attuali)"
                            >
                                Stampa PDF
                            </button>

                            {!readOnly && <button
                                onClick={openNew}
                                className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                            >
                                + Nuova non conformità
                            </button>}
                        </div>
                    </div>

                    {/* Filtri */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <div className="text-xs text-slate-500 mb-1">Stato</div>
                            <select
                                className="w-full border rounded-xl px-3 py-2"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="open">Aperte</option>
                                <option value="closed">Chiuse</option>
                            </select>
                        </div>

                        <div>
                            <div className="text-xs text-slate-500 mb-1">Da</div>
                            <input
                                type="date"
                                className="w-full border rounded-xl px-3 py-2"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </div>

                        <div>
                            <div className="text-xs text-slate-500 mb-1">A</div>
                            <input
                                type="date"
                                className="w-full border rounded-xl px-3 py-2"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </div>

                        <div className="flex items-end">
                            <button
                                onClick={load}
                                className="w-full px-4 py-2 rounded-xl border bg-white hover:bg-slate-50"
                            >
                                {loading ? "Carico…" : "Aggiorna"}
                            </button>
                        </div>
                    </div>

                    {/* Tabella */}
                    <div className="mt-5 overflow-auto border rounded-2xl">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="text-left px-3 py-2">Data</th>
                                    <th className="text-left px-3 py-2">Categoria</th>
                                    <th className="text-left px-3 py-2">Descrizione</th>
                                    <th className="text-left px-3 py-2">Stato</th>
                                    <th className="text-right px-3 py-2">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td className="px-3 py-6 text-center text-slate-500" colSpan={5}>
                                            Nessuna non conformità trovata.
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((r) => {
                                        const cat =
                                            CATEGORIES.find((c) => c.value === r.categoria)?.label || r.categoria;

                                        return (
                                            <tr key={r.id} className="border-t">
                                                <td className="px-3 py-2 whitespace-nowrap">{r.data}</td>
                                                <td className="px-3 py-2">
                                                    {cat}
                                                    {r.categoria === "altro" && r.categoria_altro ? (
                                                        <span className="text-slate-500"> — {r.categoria_altro}</span>
                                                    ) : null}
                                                </td>
                                                <td className="px-3 py-2 max-w-[520px]">
                                                    <div className="line-clamp-2">{r.descrizione}</div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <span
                                                        className={
                                                            "px-2 py-0.5 rounded-full text-xs border " +
                                                            (r.status === "open"
                                                                ? "bg-amber-50 border-amber-200 text-amber-700"
                                                                : "bg-emerald-50 border-emerald-200 text-emerald-700")
                                                        }
                                                    >
                                                        {r.status === "open" ? "Aperta" : "Chiusa"}
                                                    </span>
                                                </td>

                                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                                    <button
                                                        onClick={() => printPdfOne(r.id)}
                                                        className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 mr-2"
                                                        title="Stampa PDF di questa non conformità"
                                                    >
                                                        PDF
                                                    </button>

                                                    {!readOnly && <button
                                                        onClick={() => openEdit(r)}
                                                        className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 mr-2"
                                                    >
                                                        Apri
                                                    </button>}

                                                    {!readOnly && r.status === "open" && (
                                                        <button
                                                            onClick={() => closeNC(r.id)}
                                                            className="px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800"
                                                        >
                                                            Chiudi
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* MODAL */}
                {!readOnly && modalOpen && editing && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50">
                        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg p-4 md:p-6">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-lg font-semibold text-slate-800">
                                        {editing.id ? `Non Conformità #${editing.id}` : "Nuova Non Conformità"}
                                    </div>
                                    <div className="text-xs text-slate-500">Campi come da Allegato 4</div>
                                </div>
                                <button
                                    onClick={() => {
                                        setModalOpen(false);
                                        setEditing(null);
                                    }}
                                    className="px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50"
                                >
                                    Chiudi
                                </button>
                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <div className="text-xs text-slate-500 mb-1">Data</div>
                                    <input
                                        type="date"
                                        className="w-full border rounded-xl px-3 py-2"
                                        value={editing.data || ""}
                                        onChange={(e) => setEditing({ ...editing, data: e.target.value })}
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <div className="text-xs text-slate-500 mb-1">Non conformità rilevata da</div>
                                    <input
                                        className="w-full border rounded-xl px-3 py-2"
                                        value={editing.rilevata_da || ""}
                                        onChange={(e) => setEditing({ ...editing, rilevata_da: e.target.value })}
                                        placeholder="Es. Operatore / Addetto / Responsabile"
                                    />
                                </div>

                                <div className="md:col-span-3">
                                    <div className="text-xs text-slate-500 mb-1">Rilevazione relativa a</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <select
                                            className="w-full border rounded-xl px-3 py-2"
                                            value={editing.categoria}
                                            onChange={(e) => setEditing({ ...editing, categoria: e.target.value })}
                                        >
                                            {CATEGORIES.map((c) => (
                                                <option key={c.value} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>

                                        {editing.categoria === "altro" && (
                                            <input
                                                className="w-full border rounded-xl px-3 py-2"
                                                value={editing.categoria_altro || ""}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, categoria_altro: e.target.value })
                                                }
                                                placeholder="Specificare (Altro)"
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="md:col-span-3">
                                    <div className="text-xs text-slate-500 mb-1">
                                        Descrizione non conformità rilevata
                                    </div>
                                    <textarea
                                        className="w-full border rounded-xl px-3 py-2 min-h-[90px]"
                                        value={editing.descrizione || ""}
                                        onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })}
                                    />
                                </div>

                                <div className="md:col-span-3">
                                    <div className="text-xs text-slate-500 mb-1">Cause (ipotesi)</div>
                                    <textarea
                                        className="w-full border rounded-xl px-3 py-2 min-h-[70px]"
                                        value={editing.cause_ipotesi || ""}
                                        onChange={(e) =>
                                            setEditing({ ...editing, cause_ipotesi: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="md:col-span-3">
                                    <div className="text-xs text-slate-500 mb-1">
                                        Trattamento della non conformità
                                    </div>
                                    <textarea
                                        className="w-full border rounded-xl px-3 py-2 min-h-[70px]"
                                        value={editing.trattamento || ""}
                                        onChange={(e) => setEditing({ ...editing, trattamento: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <div className="text-xs text-slate-500 mb-1">Firma responsabile</div>
                                    <input
                                        className="w-full border rounded-xl px-3 py-2"
                                        value={editing.firma_responsabile || ""}
                                        onChange={(e) =>
                                            setEditing({ ...editing, firma_responsabile: e.target.value })
                                        }
                                    />
                                </div>

                                <div>
                                    <div className="text-xs text-slate-500 mb-1">Firma rilevatore</div>
                                    <input
                                        className="w-full border rounded-xl px-3 py-2"
                                        value={editing.firma_rilevatore || ""}
                                        onChange={(e) =>
                                            setEditing({ ...editing, firma_rilevatore: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="md:col-span-3 mt-2 border-t pt-3">
                                    <div className="text-sm font-semibold text-slate-800 mb-2">
                                        Verifica azione correttiva
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div className="md:col-span-2">
                                            <div className="text-xs text-slate-500 mb-1">
                                                Verifica dell’azione correttiva
                                            </div>
                                            <textarea
                                                className="w-full border rounded-xl px-3 py-2 min-h-[70px]"
                                                value={editing.verifica_azione_correttiva || ""}
                                                onChange={(e) =>
                                                    setEditing({
                                                        ...editing,
                                                        verifica_azione_correttiva: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>

                                        <div>
                                            <div className="text-xs text-slate-500 mb-1">Data verifica</div>
                                            <input
                                                type="date"
                                                className="w-full border rounded-xl px-3 py-2"
                                                value={editing.verifica_data || ""}
                                                onChange={(e) =>
                                                    setEditing({ ...editing, verifica_data: e.target.value })
                                                }
                                            />

                                            <div className="text-xs text-slate-500 mb-1 mt-2">
                                                Firma responsabile (verifica)
                                            </div>
                                            <input
                                                className="w-full border rounded-xl px-3 py-2"
                                                value={editing.verifica_firma_responsabile || ""}
                                                onChange={(e) =>
                                                    setEditing({
                                                        ...editing,
                                                        verifica_firma_responsabile: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 flex items-center justify-end gap-2">
                                <button
                                    onClick={() => {
                                        setModalOpen(false);
                                        setEditing(null);
                                    }}
                                    className="px-4 py-2 rounded-xl border bg-white hover:bg-slate-50"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={save}
                                    className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800"
                                >
                                    Salva
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
